export type OrderBy = 'asc' | 'desc';

export function compareByLexicalOrder(a: string, b: string, orderBy: OrderBy = 'asc'): number {
  if (orderBy === 'desc') {
    return +(a < b) - +(a > b);
  }

  return +(a > b) - +(a < b);
}

// ASCII character range for lexicographic ordering.
// Note: increase the range if the order length becomes too large.
export const MIN_CHAR_CODE = 33; // ASCII value of '!' character
export const MAX_CHAR_CODE = 127; // ASCII value of '~' character


/**
 * The following functions `rankBefore`, `rankAfter`, and `rankBetween` are the main functions
 * that manage the generation and sorting of lexical ranks.
 *
 * NOTE: This implementation is inspired by Atlassian JIRA's ranking system and Figma's implementation,
 * similar to the Go code by xissy. Source: https://github.com/xissy/lexorank/blob/master/lexorank.go
 *
 * License: MIT License (if applicable)
 */


/**
 * Generates a new rank string that is lexicographically before the given rank.
 */
export function rankBefore(rank: string): string {
  if (rank.length === 0) {
    throw new Error('Rank length must not be 0');
  }

  // Traverse the rank from right to left, attempting to decrement the rightmost character.
  // If the character is greater than (MIN_CHAR_CODE + 1), we decrement it to ensure the new rank remains valid.
  // This prevents generating a rank where a character reaches below the allowed minimum.
  for (let i = rank.length - 1; i >= 0; i--) {
    const currentCharCode = rank.charCodeAt(i);

    // Ensure the character can be safely decremented without hitting an invalid value.
    if (currentCharCode > MIN_CHAR_CODE + 1) {
      const newRank = rank.substring(0, i) + String.fromCharCode(currentCharCode - 1);
      if (!(newRank < rank)) {
        throw new Error(`Invalid rank: ${newRank} at rankBefore: ${rank}`);
      }
      return newRank;
    }
  }

  // If all characters have reached their minimum, append a new character with the lowest possible values.
  // This creates a rank that is just before the current rank, effectively decrementing the rank.
  const previousRank = rank.substring(0, rank.length - 1) +
    String.fromCharCode(MIN_CHAR_CODE) +
    String.fromCharCode(MAX_CHAR_CODE);

  if (!(previousRank < rank)) {
    throw new Error(`Invalid rank: ${previousRank} at rankBefore: ${rank}`);
  }
  return previousRank;
}

/**
 * Generates a new rank string that is lexicographically after the given rank.
 */
export function rankAfter(rank: string): string {
  if (rank.length === 0) {
    throw new Error('Rank length must not be 0');
  }

  // Traverse the rank from right to left. Starting from the least significant character,
  // the idea is to increment the rightmost character and propagate leftward if necessary.
  for (let i = rank.length - 1; i >= 0; i--) {
    const currentCharCode = rank.charCodeAt(i);
    if (currentCharCode < MAX_CHAR_CODE) {
      const newRank = rank.substring(0, i) + String.fromCharCode(currentCharCode + 1);

      if (!(newRank > rank)) {
        throw new Error(`Invalid rank: ${newRank} at rankAfter: ${rank}`);
      }
      return newRank;
    }
  }

  // If all characters in the rank have reached their maximum, append a new character at the end.
  const extendedRank = rank + String.fromCharCode(MIN_CHAR_CODE + 1);
  if (!(extendedRank > rank)) {
    throw new Error(`Invalid rank: ${extendedRank} at rankAfter: ${rank}`);
  }
  return extendedRank;
}

/**
 * Generates a new rank string that is lexicographically between the given `previousRank` and `nextRank`.
 *
 * This method ensures that the new rank is valid and falls between the provided ranks.
 * It calculates a midpoint character when possible, or extends the rank if necessary.
 */
export function rankBetween(previousRank: string, nextRank: string): string {
  if (!(previousRank < nextRank)) {
    throw new Error(`PreviousRank: ${previousRank} must be less than NextRank: ${nextRank}`);
  }

  let newRank = '';
  let needsExtension = false;
  const maxRankLength = Math.max(previousRank.length, nextRank.length);

  for (let i = 0; i < maxRankLength; i++) {
    const lowerBound = i < previousRank.length ? previousRank.charCodeAt(i) : MIN_CHAR_CODE;
    const upperBound = i < nextRank.length && !needsExtension ? nextRank.charCodeAt(i) : MAX_CHAR_CODE;

    if (lowerBound === upperBound) {
      newRank += String.fromCharCode(lowerBound); // Append the character if both ranks are equal.
    } else if (upperBound - lowerBound > 1) {
      newRank += String.fromCharCode(avg(lowerBound, upperBound)); // Calculate the midpoint.
      needsExtension = false; // Reset the extension flag. We found a valid midpoint.
      break;
    } else {
      // If characters differ by only 1, we cannot calculate a midpoint.
      // Append the lower bound and mark the need for extending the rank.
      newRank += String.fromCharCode(lowerBound);
      needsExtension = true;
    }
  }

  if (needsExtension) {
    // If no valid midpoint was found, extend the rank with a character that ensures it remains between
    // the bounds. This avoids cases where `previousRank` and `nextRank` are consecutive(e.g., 'a' and 'b').
    newRank += String.fromCharCode(avg(MIN_CHAR_CODE, MAX_CHAR_CODE));
  }

  const isValidRank = previousRank < newRank && newRank < nextRank;
  if (!isValidRank) {
    throw new Error(`Invalid rank: ${newRank} between ${previousRank} and ${nextRank}`);
  }

  return newRank;
}

function avg(n1: number, n2: number) {
  return Math.trunc((n1 + n2) / 2);
}
